// @flow
import {
  all,
  call,
  put,
  select,
  takeEvery,
  takeLatest
} from 'redux-saga/effects'
import { requestStatusUpdate } from '../components/loadingIndicator/reducer'
import { mapValuesUpdate } from '../components/map/reducer'
import { json } from 'd3.promise'
import {
  watchActiveMunicipality,
  watchAnnotatedElement
} from 'components/popup/saga'
import config from 'config'
import analyticsWatcher from './analytics'
import type { FirebaseData } from 'sources/types'
import model from 'components/selector/models'
import {
  currentExtentsUpdate,
  overallExtentsUpdate
} from 'components/scale/reducer'
import { setBrackets } from 'components/selector/reducer'
import type { AppState } from 'stores'

const apiEndpoint = config.apiEndpoint

/**
 * Root saga, triggers all other sagas
 */
export default function * rootSaga () {
  yield all([
    watchAll(),
    requestMunicipalityValues(),
    requestInitialData(),
    watchSelector(),
    watchYearSelector(),
    watchModelSelector(),
    watchActiveMunicipality(),
    watchAnnotatedElement(),
    analyticsWatcher()
  ])
}

/**
 * Watch all the changes in the selector
 * @returns {IterableIterator<*>}
 */
function * watchSelector () {
  yield takeLatest(
    action => /^SELECTOR/.test(action.type),
    requestMunicipalityValues
  )
}

/**
 * Watch changes in year
 * @returns {IterableIterator<*>}
 */
function * watchYearSelector () {
  yield takeLatest(
    action => /^SELECTOR\/YEAR/.test(action.type),
    requestOverallExtents
  )
}

/**
 * Watch changes in relationship model
 * @returns {IterableIterator<*>}
 */
function * watchModelSelector () {
  yield takeLatest(
    action => /^SELECTOR\/MODEL/.test(action.type),
    requestBrackets
  )
}

function * requestMunicipalityValues () {
  yield put(requestStatusUpdate('loading'))
  const selectedCategories = yield select(state => state.categorySelection)
  try {
    let municipalityData: FirebaseData = yield call(
      fetchValues,
      selectedCategories
    )
    yield put(mapValuesUpdate(municipalityData.municipalities))
    yield put(currentExtentsUpdate(municipalityData.extent))
    yield put(requestStatusUpdate('success'))
  } catch (err) {
    yield put(requestStatusUpdate('error'))
    console.log(err)
  }
}

function * requestOverallExtents ({ payload }) {
  yield put(requestStatusUpdate('loading'))
  try {
    let overallBracketData = yield call(fetchOverallExtents, payload)
    yield put(overallExtentsUpdate(overallBracketData))
    yield put(requestStatusUpdate('success'))
  } catch (e) {
    yield put(requestStatusUpdate('error'))
    console.log(e)
  }
}

function * requestBrackets ({ payload }) {
  yield put(requestStatusUpdate('loading'))
  let year = yield select(state => state.categorySelection.year)
  try {
    let bracketData = yield call(fetchBrackets, payload, year)
    yield put(setBrackets(bracketData))
    yield put(requestStatusUpdate('success'))
  } catch (e) {
    yield put(requestStatusUpdate('error'))
    console.log(e)
  }
}

/**
 * Request data at the start of the program
 * @returns {IterableIterator<*>}
 */
function * requestInitialData () {
  yield put(requestStatusUpdate('loading'))
  const defaults = yield select((state: AppState) => state.categorySelection)
  try {
    // Get brackets
    let bracketData = yield call(fetchBrackets, defaults.model, defaults.year)
    yield put(setBrackets(bracketData))

    // Get overall bracket
    let overallBracketData = yield call(fetchOverallExtents, defaults.year)
    yield put(overallExtentsUpdate(overallBracketData))

    yield put(requestStatusUpdate('success'))
  } catch (e) {
    yield put(requestStatusUpdate('error'))
    console.log(e)
  }
}

function fetchValues (params) {
  return json(
    `${apiEndpoint}/${incomeBracketUrlBuilder(
      params.year,
      params.model,
      params.income
    )}.json`
  )
}

function fetchOverallExtents (year) {
  return json(`${apiEndpoint}/${year}/extent.json`)
}

function fetchBrackets (model, year) {
  return json(
    `${apiEndpoint}/${availableIncomeBracketsUrlBuilder(year, model)}.json`
  )
}

/**
 * Do something with every action that takes place. Like logging them.
 */
function * watchAll () {
  if (process.env.NODE_ENV === 'development') {
    yield takeEvery('*', log)
  }
}

function * log (action) {
  let state = yield select()
  console.log(action, state)
}

// region helpers
/**
 * Maps a model enum type to a URL fragment
 * @type {[*]}
 */
const mapArray = [
  [model.SINGLE, 'single'],
  [model.RETIRED, 'retired'],
  [model.MARRIED_WITHOUT_CHILDREN, 'married/withoutChildren'],
  [model.MARRIED_WITH_CHILDREN, 'married/withChildren/singleIncome'],
  [
    model.MARRIED_WITH_CHILDREN_AND_DUAL_INCOME,
    'married/withChildren/dualIncome'
  ]
]
const modelToURLMap = new Map(mapArray)

function incomeBracketUrlBuilder (year, model, income) {
  return `${year}/${modelToURLMap.get(model)}/incomeBrackets/${income}`
}

function availableIncomeBracketsUrlBuilder (year, model) {
  return `${year}/${modelToURLMap.get(model)}/availableIncomeBrackets`
}

// endregion



// WEBPACK FOOTER //
// ./src/sagas/index.js